
import { BehaviorSubject, EMPTY, Observable, of, Subscription } from 'rxjs';
import { filter, takeUntil, map, withLatestFrom, switchMap, catchError } from 'rxjs/operators';
import { Action, MemoizedSelector, Store } from '@ngrx/store';

import { HttpErrorMessage } from '../classes';

export interface NotFoundRedirector {
    withMessage: (message: string) => NotFoundRedirector;
    switchMap: (operator: (source: NotFoundRedirection) => Observable<NotFoundRedirection>) => NotFoundRedirector;
    on: (...matches: string[]) => NotFoundRedirector;
    with: (useRedirectAction: (fromError, message) => Action) => NotFoundRedirector;
    until: (until$: Observable<any>) => NotFoundRedirector;
    subscribe: () => Subscription;
}

export type NotFoundRedirection = [ HttpErrorMessage, string ];

// Redirect to /not-found when the given store+selector emits a 404 
// use .withMessage() for specific "Not found" message
// and .until() to cancel subscription
export function redirectToNotFoundOn(store$: Store<any>, errorSelector: MemoizedSelector<object, HttpErrorMessage>): NotFoundRedirector {
	const message$ = new BehaviorSubject<string>('Errors.NotFound');
	let interceptor: (source: NotFoundRedirection) => Observable<NotFoundRedirection> = _ => of(_);
	let redirectAction: (fromError, message) => Action;
	let errors = [ 'Errors.404', 'Errors.NotFound' ];
	let obs$ = store$.select(errorSelector).pipe(
		filter(error => {
			return error && errors.some(filter => error.is(filter))
		}),
		withLatestFrom(message$),
		switchMap(_ => interceptor(_)),
		filter(Boolean),
		map(([fromError, message]) => redirectAction(fromError, message)),
		catchError(error => {
			console.error(`Not found redirection failed: ${error}`);
			return EMPTY;
		})
	);
	const redirector = {
		withMessage: (message: string) => {
			message$.next(message);
			return redirector;
		},
		switchMap: (operator: (source: NotFoundRedirection) => Observable<NotFoundRedirection>) => {
			interceptor = operator;
			return redirector;
		},
		on: (...matches: string[]) => {
			errors = errors.concat(matches);
			return redirector;
		},
		with: (useRedirectAction: (fromError, message) => Action) => {
			redirectAction = useRedirectAction;
			return redirector;
		},
		until: (until$: Observable<any>) => {
			obs$ = obs$.pipe(takeUntil(until$));
			return redirector;
		},
		subscribe: () => obs$.subscribe(action => {
			store$.dispatch(action)
		})
	};
	return redirector;
}
